import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Settings,
    User,
    LogOut,
    ChevronDown,
    Menu,
    X,
    School,
    Sun,
    Moon,
    Clock,
    Search,
    BookOpen,
    Layers
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import NotificationBell from '../Layout/NotificationBell';
import { getFileUrl } from '../../utils/api';

const BRAND_DARK = '#00152E';
const BRAND_BLUE = '#1a3884';

const GlobalHeader = ({ onMenuToggle, isSidebarOpen }) => {
    const { user, logout } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();

    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [showSearchResults, setShowSearchResults] = useState(false);


    const profileRef = useRef(null);
    const searchRef = useRef(null);

    // Live clock — ticks every second
    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    const formatTime = (date) =>
        date.toLocaleTimeString(typeof navigator !== 'undefined' ? navigator.language : 'en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
        });

    const formatDate = (date) =>
        date.toLocaleDateString(typeof navigator !== 'undefined' ? navigator.language : 'en-US', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
        });

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (profileRef.current && !profileRef.current.contains(event.target)) {
                setShowProfileMenu(false);
            }
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowSearchResults(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Search with debounce
    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            if (searchQuery.trim()) {
                performSearch(searchQuery);
            } else {
                setSearchResults([]);
            }
        }, 300);
        return () => clearTimeout(delayDebounce);
    }, [searchQuery]);

    const performSearch = async (query) => {
        const quickLinks = [
            { name: 'Dashboard', type: 'page', link: '/dashboard', subtitle: 'Overview, analytics & metrics' },
            { name: 'Institutes / Colleges', type: 'page', link: '/colleges', subtitle: 'Manage academic institutions & campus codes' },
            { name: 'Users / Staff Management', type: 'page', link: '/users', subtitle: 'Manage administrators & support staff' },
            { name: 'Student Onboarding', type: 'page', link: '/admin/student-onboarding', subtitle: 'Onboard and manage students globally' },
            { name: 'Courses', type: 'page', link: '/courses', subtitle: 'Manage learning flows, curriculum & content modules' },
            { name: 'Assessments', type: 'page', link: '/assessments', subtitle: 'Manage student tests, SJT questions & submissions' },
            { name: 'Access Logs / Audit Logs', type: 'page', link: '/admin/access-logs', subtitle: 'Authentication security events, logins & failures' },
            { name: 'Analytics', type: 'page', link: '/analytics', subtitle: 'Performance charts, system metrics & active counts' },
            { name: 'Community Hub', type: 'page', link: '/admin/community', subtitle: 'Social feeds, announcements & discussion threads' },
            { name: 'IT Support', type: 'page', link: '/it-support', subtitle: 'Contact IT helpdesk & live support personnel' },
        ];

        const queryLower = query.toLowerCase();
        const matchingQuickLinks = quickLinks.filter(
            (item) =>
                item.name.toLowerCase().includes(queryLower) ||
                item.subtitle.toLowerCase().includes(queryLower)
        );

        try {
            const api = (await import('../../utils/api')).default;
            const response = await api.get(`/search?query=${encodeURIComponent(query)}`);
            if (response.data.success) {
                const combined = [...matchingQuickLinks, ...response.data.data];
                setSearchResults(combined);
                setShowSearchResults(combined.length > 0);
            } else {
                setSearchResults(matchingQuickLinks);
                setShowSearchResults(matchingQuickLinks.length > 0);
            }
        } catch (error) {
            console.error('Error searching:', error);
            setSearchResults(matchingQuickLinks);
            setShowSearchResults(matchingQuickLinks.length > 0);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    return (
        <header
            className="sticky top-0 z-40 bg-white dark:bg-[#00152E] border-b border-gray-200 dark:border-[#1a3884]/40 shadow-sm transition-colors duration-300"
        >
            <div className="flex items-center justify-between px-4 lg:px-6 py-3">

                {/* Left — Menu toggle + Search */}
                <div className="flex items-center gap-3 flex-1 min-w-0">
                    {/* Mobile Menu Toggle — every role now has a sidebar, so this always shows on mobile */}
                    <button
                        onClick={onMenuToggle}
                        className="lg:hidden p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors text-gray-700 dark:text-white"
                        aria-label="Toggle menu"
                    >
                        {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>

                    <div className="relative flex-1 max-w-md lg:max-w-lg" ref={searchRef}>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-300" size={18} />
                            <input
                                type="text"
                                placeholder="Search users, colleges, courses..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onFocus={() => searchQuery && setShowSearchResults(true)}
                                className="w-full pl-9 pr-4 py-2.5 bg-gray-50 dark:bg-white/10 border border-gray-200 dark:border-white/20 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-[#1a3884]/40 dark:focus:ring-white/40 focus:border-transparent transition-all"
                            />
                        </div>

                        {showSearchResults && searchResults.length > 0 && (
                            <div className="absolute top-full mt-2 w-full bg-white dark:bg-[#002147] rounded-lg shadow-lg border border-gray-200 dark:border-[#1a3884]/30 py-2 max-h-96 overflow-y-auto z-50">
                                {searchResults.map((result, index) => (
                                    <Link
                                        key={index}
                                        to={result.link}
                                        onClick={() => { setShowSearchResults(false); setSearchQuery(''); }}
                                        className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                                    >
                                        <div className="w-9 h-9 bg-gradient-to-br from-[#00152E] to-[#1a3884] rounded-lg flex items-center justify-center text-white flex-shrink-0">
                                            {result.type === 'user' && <User size={16} />}
                                            {result.type === 'college' && <School size={16} />}
                                            {result.type === 'course' && <BookOpen size={16} />}
                                            {result.type === 'department' && <School size={16} />}
                                            {result.type === 'page' && <Layers size={16} />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{result.name}</p>
                                            <p className="text-xs text-gray-500 dark:text-slate-400 truncate">{result.subtitle}</p>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Right — Clock + Theme + Bell + Profile */}
                <div className="flex items-center gap-2 lg:gap-3 ml-3">

                    {/* Live Clock pill — desktop only */}
                    <div className="hidden lg:flex items-center gap-2 pl-3 pr-4 py-2 bg-white dark:bg-white/10 border border-gray-200 dark:border-white/20 rounded-xl shadow-sm select-none relative overflow-hidden">
                        {/* left accent bar */}
                        <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-gray-200 dark:bg-white/40 rounded-l-xl" />
                        <Clock size={16} className="text-[#1a3884] dark:text-blue-200 flex-shrink-0 ml-1" />
                        <span className="text-sm font-semibold text-gray-800 dark:text-white tabular-nums tracking-tight mt-[1px]">
                            {formatTime(currentTime)}
                        </span>
                    </div>

                    {/* Theme Toggle */}
                    <button
                        onClick={toggleTheme}
                        className="p-2 text-gray-500 dark:text-white hover:bg-gray-100 dark:hover:bg-white/10 hover:text-gray-700 rounded-full transition-all duration-300 hover:rotate-12"
                        aria-label="Toggle theme"
                    >
                        {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                    </button>

                    {/* Notifications */}
                    <NotificationBell />

                    {/* Profile Menu */}
                    <div className="relative" ref={profileRef}>
                        <button
                            onClick={() => setShowProfileMenu(!showProfileMenu)}
                            className="flex items-center gap-2 lg:gap-2.5 p-1.5 hover:bg-gray-50 dark:hover:bg-white/10 rounded-lg transition-colors"
                        >
                            <div className={`w-8 h-8 lg:w-9 lg:h-9 rounded-full flex items-center justify-center font-bold text-sm shadow-md ring-2 ring-gray-100 dark:ring-white/20 overflow-hidden shrink-0 ${user?.profileImage || (user?.role === 'college_admin' && user?.college?.logo) ? 'bg-white' : 'bg-[#1a3884] text-white'}`}>
                                {user?.profileImage ? (
                                    <img src={getFileUrl(user.profileImage)} className="w-full h-full object-cover rounded-full" alt="Profile" />
                                ) : user?.role === 'college_admin' && user?.college?.logo ? (
                                    <img src={getFileUrl(user.college.logo)} className="w-full h-full object-cover rounded-full" alt="College Logo" />
                                ) : (
                                    user?.fullName?.charAt(0) || 'A'
                                )}
                            </div>
                            <div className="hidden lg:block text-left">
                                <p className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">{user?.fullName || 'Admin'}</p>
                                <p className="text-[11px] text-gray-500 dark:text-blue-200 font-medium capitalize leading-tight">{user?.role || 'Administrator'}</p>
                            </div>
                            <ChevronDown size={14} className="hidden lg:block text-gray-400 dark:text-blue-200" />
                        </button>

                        {showProfileMenu && (
                            <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-[#002147] rounded-xl shadow-xl border border-gray-200 dark:border-[#1a3884]/30 overflow-hidden z-50">
                                <div className="p-4 border-b border-gray-100 dark:border-[#1a3884]/30 bg-gradient-to-r from-[#00152E]/5 to-[#1a3884]/5 dark:from-[#002A5C] dark:to-[#00152E]">
                                    <p className="text-sm font-bold text-gray-900 dark:text-white">{user?.fullName || 'Admin'}</p>
                                    <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5 mb-1.5 truncate">{user?.email}</p>
                                    {(user?.companyId || user?.recruiterId) && (
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {user?.companyId && (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#00152E] text-white tracking-wide">
                                                    {user.companyId}
                                                </span>
                                            )}
                                            {user?.recruiterId && !user?.isCompanyModel && (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#1a3884] text-white tracking-wide">
                                                    {user.recruiterId}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="py-1.5">
                                    <Link
                                        to="/profile"
                                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                                        onClick={() => setShowProfileMenu(false)}
                                    >
                                        <User size={16} className="text-gray-500 dark:text-slate-400" />
                                        <span className="text-sm text-gray-700 dark:text-gray-300">My Profile</span>
                                    </Link>
                                    <Link
                                        to="/settings"
                                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                                        onClick={() => setShowProfileMenu(false)}
                                    >
                                        <Settings size={16} className="text-gray-500 dark:text-slate-400" />
                                        <span className="text-sm text-gray-700 dark:text-gray-300">Settings</span>
                                    </Link>
                                </div>
                                <div className="border-t border-gray-100 dark:border-[#1a3884]/30">
                                    <button
                                        onClick={handleLogout}
                                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors w-full text-left"
                                    >
                                        <LogOut size={16} className="text-red-500 dark:text-red-400" />
                                        <span className="text-sm text-red-500 dark:text-red-400 font-semibold">Logout</span>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
};

export default GlobalHeader;